import React from "react";
var __jsx = React.createElement;
import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import UniqueSelectionInput from "@modules/shared/components/unique-selection-input";
import useAfterInitEffect from "@lib/hooks/use-after-initialization-effect";
import { useMobileFilters } from "@lib/context/mobile/filters-context";
import { IconButton, MuscleButton } from "@modules/shared/components/buttons";
import InformativeCard from "@modules/shared/components/cards/informative";
import MuscleQuantityTogglerInput from "@modules/shared/components/quantity-toggler-input";
import { validatePassengersDetails } from "@lib/util/flights/input-validation-ops";
import { useSideOverPanel } from "@modules/mobile/hooks/useSideOverPanel";
import { XMarkIcon } from "@heroicons/react/24/outline";
import useCssModules from "@lib/hooks/use-css-modules";
import { usePathname } from "next/navigation";
import { useForwardNode } from "@modules/mobile/hooks/useForwardNode";
import Icons from "@modules/icons";
import { cn } from "@lib/cn";
export const PassengerSelectionView = () => {
  var _process$env$NEXT_PUB;
  const t = useTranslations("Flights");
  const {
    flightFilters
  } = useMobileFilters();
  const {
    togglePanel
  } = useSideOverPanel();
  const {
    setNode
  } = useForwardNode();
  const {
    cssModules
  } = useCssModules();
  const pathname = usePathname();
  const allowBabyOnSeat = ((_process$env$NEXT_PUB = process.env.NEXT_PUBLIC_ALLOW_BABY_ON_SEAT) !== null && _process$env$NEXT_PUB !== void 0 ? _process$env$NEXT_PUB : "false") === "true";
  const {
    passengerSelectionTitle,
    passengerSelectionHeaderTitle,
    passengerSelectionHeaderButton,
    passengersApplyButtonContainer,
    passengersCabinClassContainer,
    passengerSelectionHeaderContainer,
    passengerSelectionHeaderIconButtonContainer,
    passengerSelectionPanelContainer
  } = (cssModules === null || cssModules === void 0 ? void 0 : cssModules.views.mobile.PassengerSelection) || {};
  const {
    exitIconClassName,
    clearTextClassName,
    clearIconClassName
  } = (cssModules === null || cssModules === void 0 ? void 0 : cssModules.globals.sideOverPanel) || {};
  const {
    dividerClassName
  } = (cssModules === null || cssModules === void 0 ? void 0 : cssModules.globals.dividers) || {};
  const {
    0: adults,
    1: setAdults
  } = useState(flightFilters.passengersDetails.adults);
  const {
    0: children,
    1: setChildren
  } = useState(flightFilters.passengersDetails.children);
  const {
    0: infants,
    1: setInfants
  } = useState(flightFilters.passengersDetails.infants);
  const {
    0: infantsWithSeat,
    1: setInfantsWithSeat
  } = useState(flightFilters.passengersDetails.infants_with_seat);
  const {
    0: errorMessage,
    1: setErrorMessage
  } = useState("");
  const {
    0: isPassengerCountValid,
    1: setIsPassengerCountValid
  } = useState({
    totalPassengerQtyValid: true,
    enoughAdultsForInfants: true,
    enoughAdultsForChildren: true,
    enoughTotalAdults: true,
    atLeastOneAdult: true
  });

  // Sync local state with global state
  useEffect(() => {
    setAdults(flightFilters.passengersDetails.adults);
    setChildren(flightFilters.passengersDetails.children);
    setInfants(flightFilters.passengersDetails.infants);
    setInfantsWithSeat(flightFilters.passengersDetails.infants_with_seat);
  }, [flightFilters.passengersDetails]);
  useEffect(() => {
    const validation = validatePassengersDetails({
      adults,
      children,
      infants,
      infants_with_seat: infantsWithSeat
    });
    setIsPassengerCountValid(validation.validationValues);
    setErrorMessage(validation.validation.success ? "" : t(validation.validation.message));
    flightFilters.setHasPassengerError(!validation.validation.success);
  }, [adults, children, infants, infantsWithSeat, flightFilters]);
  const updatePassengerCount = (type, increment) => {
    const updateFunction = {
      adults: setAdults,
      children: setChildren,
      infants: setInfants,
      infants_with_seat: setInfantsWithSeat
    };
    updateFunction[type](prev => {
      const newValue = Math.max(0, prev + (increment ? 1 : -1));

      // Update the global context immediately
      const updatedPassengersDetails = {
        adults: type === "adults" ? newValue : adults,
        children: type === "children" ? newValue : children,
        infants: type === "infants" ? newValue : infants,
        infants_with_seat: type === "infants_with_seat" ? newValue : infantsWithSeat
      };
      flightFilters.setPassengersDetails(updatedPassengersDetails);
      return newValue;
    });
  };
  useAfterInitEffect(() => {
    if (!flightFilters.isFlightsOpen) {
      if (flightFilters.hasPassengerError) {
        flightFilters.setIsFlightsOpen(true);
      }
    }
  }, [flightFilters.isFlightsOpen, flightFilters.hasPassengerError, flightFilters.setIsFlightsOpen]);
  const handleClear = () => {
    const defaultPassengersDetails = {
      adults: 1,
      children: 0,
      infants: 0,
      infants_with_seat: 0
    };
    setAdults(1);
    setChildren(0);
    setInfants(0);
    setInfantsWithSeat(0);

    // Update the global context
    flightFilters.setPassengersDetails(defaultPassengersDetails);
    // Note: Cabin is not reset as it should maintain the current selection
  };
  return __jsx("div", {
    className: passengerSelectionPanelContainer
  }, __jsx("div", {
    className: passengerSelectionHeaderContainer
  }, __jsx("div", {
    className: passengerSelectionHeaderIconButtonContainer
  }, __jsx(IconButton, {
    icon: __jsx(XMarkIcon, {
      className: exitIconClassName,
      "aria-hidden": "true"
    }),
    onClickButton: () => togglePanel()
  }), __jsx("h2", {
    className: passengerSelectionHeaderTitle
  }, t("PASSENGERS"))), __jsx("button", {
    onClick: handleClear,
    className: cn(clearTextClassName, 'flex items-center gap-1')
  }, __jsx(Icons.trashcanV2, {
    className: clearIconClassName
  }), t("CLEAR"))), __jsx("div", {
    className: "flex flex-col"
  }, __jsx("div", {
    className: "space-y-[12px] px-[22px]"
  }, __jsx("h3", {
    className: passengerSelectionTitle
  }, t("PASSENGERS")), __jsx(MuscleQuantityTogglerInput, {
    dataTestId: "flight-search-passengers-quantity-selector-adults-input",
    label: t("PASSENGERS_TYPE.ADULTS"),
    description: t("PASSENGERS_TYPE.ADULTS_DESCRIPTION"),
    value: adults,
    onIncrement: () => updatePassengerCount("adults", true),
    onDecrement: () => updatePassengerCount("adults", false),
    incrementDisabled: !isPassengerCountValid.totalPassengerQtyValid,
    decrementDisabled: adults === 0
  }), __jsx(MuscleQuantityTogglerInput, {
    dataTestId: "flight-search-passengers-quantity-selector-children-input",
    label: t("PASSENGERS_TYPE.CHILDREN"),
    description: t("PASSENGERS_TYPE.CHILDREN_DESCRIPTION"),
    value: children,
    onIncrement: () => updatePassengerCount("children", true),
    onDecrement: () => updatePassengerCount("children", false),
    incrementDisabled: !isPassengerCountValid.enoughAdultsForChildren || !isPassengerCountValid.totalPassengerQtyValid,
    decrementDisabled: children === 0
  }), __jsx(MuscleQuantityTogglerInput, {
    dataTestId: "flight-search-passengers-quantity-selector-infants-input",
    label: t("PASSENGERS_TYPE.BABIES_ON_LAP"),
    description: t("PASSENGERS_TYPE.BABIES_DESCRIPTION"),
    value: infants,
    onIncrement: () => updatePassengerCount("infants", true),
    onDecrement: () => updatePassengerCount("infants", false),
    decrementDisabled: infants === 0,
    incrementDisabled: !isPassengerCountValid.enoughAdultsForInfants || !isPassengerCountValid.totalPassengerQtyValid
  }), allowBabyOnSeat && __jsx(MuscleQuantityTogglerInput, {
    dataTestId: "flight-search-passengers-quantity-selector-infants-with-seat-input",
    label: t("PASSENGERS_TYPE.BABIES_WITH_SEAT"),
    description: t("PASSENGERS_TYPE.BABIES_DESCRIPTION"),
    value: infantsWithSeat,
    onIncrement: () => updatePassengerCount("infants_with_seat", true),
    onDecrement: () => updatePassengerCount("infants_with_seat", false),
    decrementDisabled: infantsWithSeat === 0,
    incrementDisabled: !isPassengerCountValid.enoughAdultsForInfants || !isPassengerCountValid.totalPassengerQtyValid
  }), errorMessage && __jsx(InformativeCard, {
    message: errorMessage,
    severity: "danger"
  })), __jsx("div", {
    className: cn(dividerClassName, 'mt-4 mb-4')
  }), __jsx("div", {
    className: passengersCabinClassContainer
  }, __jsx("h3", {
    className: passengerSelectionTitle
  }, t("CABIN")), __jsx(UniqueSelectionInput, {
    dataTestId: "flight-search-passengers-and-cabin-fly-over-cabin",
    options: flightFilters.cabinOptions,
    radioSize: "xl",
    onSelectOption: option => {
      console.log("🔄 Cabin class selected:", option.key);
      flightFilters.handleSetCabin(option.key);
    },
    value: flightFilters.cabinOptions.find(option => option.key === flightFilters.selectedCabin),
    radioFirst: true
  }))), __jsx("div", {
    className: passengersApplyButtonContainer
  }, __jsx(MuscleButton, {
    onClick: () => togglePanel(),
    variant: "solidXL"
  }, t("APPLY")), pathname.includes("/flights/select") && __jsx(MuscleButton, {
    variant: "outlineXL",
    onClick: () => setNode("EditFlightSelection")
  }, t("MAKE_CHANGES"))));
};